import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { EnableEscCloseDirective, UiButtonComponent, UiModels, UiPreloaderComponent, UiSearchComponent } from '@ui';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../store/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { filter, take } from 'rxjs';
import { VehicleGroupsDialogService } from '../../../../../../services/vehicle-groups-dialog.service';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { SaveVehiclesDialogComponent } from '../save-vehicles-dialog/save-vehicles-dialog.component';
import { VehicleModels } from '@models/vehicle.model';

@Component({
  selector: 'app-add-vehicle-to-groups-dialog',
  standalone: true,
  imports: [
    MatCheckbox,
    ReactiveFormsModule,
    TranslatePipe,
    UiButtonComponent,
    UiSearchComponent,
    UiPreloaderComponent,
  ],
  templateUrl: './add-vehicles-to-groups-dialog.component.html',
  styleUrl: './add-vehicles-to-groups-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class AddVehiclesToGroupsDialogComponent implements OnInit {
  private readonly store$ = inject(Store<AppState>);
  private readonly actions$ = inject(Actions);
  private readonly vehicleGroupsDialogService = inject(VehicleGroupsDialogService);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject<VehicleGroupsModel.AddVehicleToGroupsDialogData>(MAT_DIALOG_DATA);

  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly searchQuery = signal('');
  private readonly selectedGroupIds = signal<Set<string>>(new Set<string>());

  private readonly groups = this.store$.selectSignal(vehicleGroupsFeature.selectAllVehicleGroups);
  private readonly vehicleGroupsState = this.store$.selectSignal(vehicleGroupsFeature.selectVehicleGroups);
  protected readonly preloaderColor = UiModels.PreloaderColor;
  protected readonly isVehicleGroupsLoading = computed(() => this.vehicleGroupsState().isLoading);
  protected readonly isAddingVehiclesToGroups = this.store$.selectSignal(vehicleGroupsFeature.selectIsAddingVehiclesToGroups);
  protected readonly saveDisabled = computed(
    () => this.selectedGroupIds().size === 0 || this.isAddingVehiclesToGroups(),
  );

  protected readonly filteredGroups = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const groups = this.groups();

    if (!query) {
      return groups;
    }

    return groups.filter((group) => group.name.toLowerCase().includes(query));
  });

  public constructor(
    private readonly dialogRef: MatDialogRef<AddVehiclesToGroupsDialogComponent>,
  ) {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((query) => this.searchQuery.set(query));
  }

  public ngOnInit(): void {
    this.store$.dispatch(VehicleGroupsActions.loadVehicleGroups());
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    if (this.saveDisabled()) {
      return;
    }

    const groupIds = [...this.selectedGroupIds()];

    if (this.data.vehicleType === VehicleGroupsModel.AddVehicleToGroupDialogTab.UnsavedVehicles &&
      this.data.unsavedVehicles?.length) {
      this.dialog
        .open<SaveVehiclesDialogComponent, VehicleModels.SaveVehiclesDialogData, boolean>(
          SaveVehiclesDialogComponent,
          {
            width: '820px',
            panelClass: 'modal-no-padding',
            maxHeight: '80vh',
            disableClose: false,
            data: {
              vehicles: this.data.unsavedVehicles,
              groupIds,
              savedVehicleIds: this.getSelectedSavedVehicleIds(),
            },
          },
        )
        .afterClosed()
        .pipe(
          filter(Boolean),
          take(1),
        )
        .subscribe(() => this.dialogRef.close(true));
      return;
    }

    this.actions$
      .pipe(
        ofType(VehicleGroupsActions.addVehiclesToGroupsSuccess),
        filter((action) => this.hasSameGroupIds(action.groupIds, groupIds)),
        take(1),
      )
      .subscribe(() => {
        this.dialogRef.close(true);
      });

    this.store$.dispatch(
      VehicleGroupsActions.startAddingVehiclesToGroups({
        groupIds,
        savedVehicleIds: this.getSelectedSavedVehicleIds(),
        unsavedVehicleDtos: this.getSelectedUnsavedVehicleDtos(),
      }),
    );
  }

  protected isGroupSelected(groupId: string): boolean {
    return this.selectedGroupIds().has(groupId);
  }

  protected setGroupSelected(groupId: string, selected: boolean): void {
    this.selectedGroupIds.update((selectedGroupIds) => {
      const nextSelectedGroupIds = new Set(selectedGroupIds);

      if (selected) {
        nextSelectedGroupIds.add(groupId);
      } else {
        nextSelectedGroupIds.delete(groupId);
      }

      return nextSelectedGroupIds;
    });
  }

  protected toggleGroup(groupId: string): void {
    this.setGroupSelected(groupId, !this.isGroupSelected(groupId));
  }

  protected openCreateGroupDialog(): void {
    this.vehicleGroupsDialogService.openCreate()
      .pipe(take(1))
      .subscribe((group) => {
        if (group) {
          this.setGroupSelected(group._id, true);
        }
      });
  }

  private getSelectedSavedVehicleIds(): number[] {
    return this.data.vehicleType === VehicleGroupsModel.AddVehicleToGroupDialogTab.ExistingVehicles
      ? this.data.vehicleIds
      : [];
  }

  private getSelectedUnsavedVehicleDtos(): VehicleModels.UnsavedVehicleDto[] {
    return this.data.vehicleType === VehicleGroupsModel.AddVehicleToGroupDialogTab.UnsavedVehicles
      ? this.data.unsavedVehicleDtos ?? []
      : [];
  }

  private hasSameGroupIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const sortedLeft = [...left].sort();
    const sortedRight = [...right].sort();

    return sortedLeft.every((id, index) => id === sortedRight[index]);
  }
}
